import React, { useEffect, useState } from "react";
import styles from "./UsuarioFormModal.module.css";
import type { NivelAcesso, Usuario, UsuarioFormValues } from "../../types/UsuarioTypes";

interface UsuarioFormModalProps {
  usuario: Usuario | null; // null = criação
  niveisAcesso: NivelAcesso[];
  carregando: boolean;
  erro: string | null;
  onSalvar: (valores: UsuarioFormValues) => void;
  onFechar: () => void;
}

const CPF_REGEX = /^\d{11}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Erros = Partial<Record<keyof UsuarioFormValues, string>>;

const valoresIniciais = (usuario: Usuario | null): UsuarioFormValues => ({
  id: usuario?.id,
  nome: usuario?.nome ?? "",
  login: usuario?.login ?? "",
  email: usuario?.email ?? "",
  cpf: usuario?.cpf ?? "",
  nivel: usuario?.nivel ?? "",
});

const UsuarioFormModal: React.FC<UsuarioFormModalProps> = ({
  usuario,
  niveisAcesso,
  carregando,
  erro,
  onSalvar,
  onFechar,
}) => {
  const [valores, setValores] = useState<UsuarioFormValues>(
    valoresIniciais(usuario),
  );
  const [erros, setErros] = useState<Erros>({});
  const ehEdicao = usuario !== null;

  useEffect(() => {
    setValores(valoresIniciais(usuario));
    setErros({});
  }, [usuario]);

  function atualizarCampo<K extends keyof UsuarioFormValues>(
    campo: K,
    valor: UsuarioFormValues[K],
  ) {
    setValores((atual) => ({ ...atual, [campo]: valor }));
  }

  function validar(): boolean {
    const novosErros: Erros = {};

    if (!valores.nome.trim()) novosErros.nome = "Informe o nome completo";
    if (!valores.login.trim()) novosErros.login = "Informe o login";
    if (!EMAIL_REGEX.test(valores.email)) novosErros.email = "E-mail inválido";
    if (!CPF_REGEX.test(valores.cpf.replace(/\D/g, "")))
      novosErros.cpf = "CPF deve conter 11 dígitos";
    if (!valores.nivel) novosErros.nivel = "Selecione o nível de acesso";

    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSalvar({ ...valores, cpf: valores.cpf.replace(/\D/g, "") });
  }

  console.log(niveisAcesso)

  return (
    <div className={styles.overlay} role="presentation" onClick={onFechar}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="usuario-form-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className={styles.cabecalho}>
          <h2 id="usuario-form-title">
            {ehEdicao ? "Editar usuário" : "Novo usuário"}
          </h2>
          <button
            type="button"
            className={styles.fechar}
            onClick={onFechar}
            aria-label="Fechar"
          >
            ×
          </button>
        </header>

        <form onSubmit={handleSubmit} className={styles.formulario} noValidate>
          {erro && <div className={styles.erroGeral}>{erro}</div>}

          <div className={styles.campo}>
            <label htmlFor="nome">Nome completo</label>
            <input
              id="nome"
              type="text"
              value={valores.nome}
              onChange={(e) => atualizarCampo("nome", e.target.value)}
              className={erros.nome ? styles.inputComErro : undefined}
              autoFocus
            />
            {erros.nome && (
              <span className={styles.textoErro}>{erros.nome}</span>
            )}
          </div>

          <div className={styles.linhaDupla}>
            <div className={styles.campo}>
              <label htmlFor="login">Login</label>
              <input
                id="login"
                type="text"
                value={valores.login}
                onChange={(e) => atualizarCampo("login", e.target.value)}
                className={erros.login ? styles.inputComErro : undefined}
              />
              {erros.login && (
                <span className={styles.textoErro}>{erros.login}</span>
              )}
            </div>
          </div>

          <div className={styles.campo}>
            <label htmlFor="email">E-mail</label>
            <input
              id="email"
              type="email"
              value={valores.email}
              onChange={(e) => atualizarCampo("email", e.target.value)}
              className={erros.email ? styles.inputComErro : undefined}
            />
            {erros.email && (
              <span className={styles.textoErro}>{erros.email}</span>
            )}
          </div>

          <div className={styles.campo}>
            <label htmlFor="nivel">Nível de acesso</label>
            <select
              id="nivel"
              value={valores.nivel}
              onChange={(e) => atualizarCampo("nivel", e.target.value)}
              className={erros.nivel ? styles.inputComErro : undefined}
            >
              <option value="">Selecione…</option>
              {niveisAcesso.map((n) => (
                <option key={n.id} value={n.acesso}>
                  {n.nivel}
                </option>
              ))}
            </select>
            {erros.nivel && (
              <span className={styles.textoErro}>{erros.nivel}</span>
            )}
          </div>

          <footer className={styles.rodape}>
            <button
              type="button"
              className={styles.botaoSecundario}
              onClick={onFechar}
              disabled={carregando}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className={styles.botaoPrimario}
              disabled={carregando}
            >
              {carregando
                ? "Salvando…"
                : ehEdicao
                  ? "Salvar alterações"
                  : "Cadastrar usuário"}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
};

export default UsuarioFormModal;
